import { ChainSymbol, chainMapping } from "../enums/blockchains";
import { ICommonBase } from "../interfaces/common";

export interface IMappingItem {
  id: number;
  title: string;
  slug: ChainSymbol;
  name: string;
  scan: string;
  walletScan: string;
  tokenScan: `${string}/tokenName${string}`;
  coingecko: string;
  covalent: string; // null - covalent
  trustwallet: string;
  hex: `0x${string}`;
  symbol: ChainSymbol;
  nativeCurrency: string;
  rpc: string;
  commonBases: ICommonBase[];
  image: string;
}

type IMapping = {
  [key in string]: IMappingItem;
};

const mapping: IMapping = {
  [chainMapping.ETH]: {
    id: 1,
    title: 'Ethereum',
    slug: 'ETH',
    name: "Mainnet Ethereum",
    scan: "https://etherscan.io/tx/",
    walletScan: "https://etherscan.io/address/",
    tokenScan: `https://etherscan.io/token/tokenName`,
    coingecko: "ethereum",
    covalent: "ETH",
    trustwallet: "ethereum",
    hex: "0x1",
    symbol: "ETH",
    nativeCurrency: "ETH",
    rpc: '',
    image: 'https://kattana-icons.s3.amazonaws.com/chains/ethereum.svg',
    commonBases: [
      {
        title: "WETH",
        address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
        decimals: 18,
      },
      {
        title: "USDT",
        address: "0xdac17f958d2ee523a2206206994597c13d831ec7",
        decimals: 6,
      },
      {
        title: "DAI",
        address: "0x6b175474e89094c44da98b954eedeac495271d0f",
        decimals: 18,
      },
      {
        title: "USDC",
        address: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
        decimals: 6,
      },
    ],
  },
  [chainMapping.BSC]: {
    id: 56,
    title: 'BSC',
    slug: 'BSC',
    name: "Binance Smart Chain",
    scan: "https://bscscan.com/tx/",
    walletScan: "https://bscscan.com/address/",
    tokenScan: `https://bscscan.com/token/tokenName`,
    coingecko: "binance-smart-chain",
    covalent: "BNB",
    trustwallet: "smartchain",
    hex: "0x38",
    symbol: "BSC",
    nativeCurrency: "BNB",
    rpc: "https://bsc-dataseed.binance.org",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/bsc_chain.svg',
    commonBases: [
      {
        title: "WBNB",
        address: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
        decimals: 18,
      },
      {
        title: "BUSD",
        address: "0xe9e7cea3dedca5984780bafc599bd69add087d56",
        decimals: 18,
      },
    ],
  },
  [chainMapping.BSC_TESTNET]: {
    id: 97,
    title: 'BSC Testnet',
    slug: 'BSC_TESTNET',
    name: "Binance Smart Chain Testnet",
    scan: "https://testnet.bscscan.com/tx/",
    walletScan: "https://testnet.bscscan.com/address/",
    tokenScan: `https://testnet.bscscan.com/token/tokenName`,
    coingecko: "binance-smart-chain-testnet",
    covalent: "BNB",
    trustwallet: "smartchain-testnet",
    hex: "0x61",
    symbol: "BSC_TESTNET",
    nativeCurrency: "BNB",
    rpc: "https://data-seed-prebsc-1-s1.binance.org:8545",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/bsc_chain.svg',
    commonBases: [],
  },
  [chainMapping.METER]: {
    id: 82,
    title: 'Meter',
    slug: 'METER',
    name: "Meter.io",
    scan: "https://scan.meter.io/tx/",
    walletScan: "https://scan.meter.io/address/",
    tokenScan: `https://scan.meter.io/address/tokenName`,
    coingecko: "meter",
    covalent: "METER",
    trustwallet: "meterio",
    hex: "0x52",
    symbol: "METER",
    nativeCurrency: "MTR",
    rpc: "https://rpc.meter.io",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/meter.svg',
    commonBases: [],
  },
  [chainMapping.POLYGON]: {
    id: 137,
    title: 'Polygon',
    slug: 'POLYGON',
    name: "Polygon",
    scan: "https://polygonscan.com/tx/",
    walletScan: "https://polygonscan.com/address/",
    tokenScan: `https://polygonscan.com/token/tokenName`,
    coingecko: "polygon-pos",
    covalent: "MATIC",
    trustwallet: "polygon",
    hex: "0x89",
    symbol: "POLYGON",
    nativeCurrency: "MATIC",
    rpc: "https://polygon-rpc.com",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/polygon.svg',
    commonBases: [
      {
        title: "WMATIC",
        address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270",
        decimals: 18,
      },
      {
        title: "QUICK",
        address: "0x831753dd7087cac61ab5644b308642cc1c33dc13",
        decimals: 18,
      },
      {
        title: "WETH",
        address: "0x7ceb23fd6bc0add59e62ac25578270cff1b9f619",
        decimals: 18,
      },
      {
        title: "USDC",
        address: "0x2791bca1f2de4661ed88a30c99a7a9449aa84174",
        decimals: 6,
      },
      {
        title: "DAI",
        address: "0x8f3cf7ad23cd3cadbd9735aff958023239c6a063",
        decimals: 18,
      },
    ],
  },
  [chainMapping.HECO]: {
    id: 128,
    title: 'Heco',
    slug: 'HECO',
    name: "HECO",
    scan: "https://hecoinfo.com/tx/",
    walletScan: "https://hecoinfo.com/address/",
    tokenScan: `https://hecoinfo.com/token/tokenName`,
    coingecko: "huobi-token",
    covalent: "HECO",
    trustwallet: "heco",
    hex: "0x80",
    symbol: "HECO",
    nativeCurrency: "HT",
    rpc: "https://http-mainnet.hecochain.com",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/heco.svg',
    commonBases: [
      {
        title: "WHT",
        address: "0x5545153CCFcA01fbd7Dd11C0b23ba694D9509A6F",
        decimals: 18,
      },
      {
        title: "HUSD",
        address: "0x0298c2b32eae4da002a15f36fdf7615bea3da047",
        decimals: 6,
      },
      {
        title: "HBTC",
        address: "0x66a79d23e58475d2738179ca52cd0b41d73f0bea",
        decimals: 18,
      },
      {
        title: "ETH",
        address: "0x64ff637fb478863b7468bc97d30a5bf3a428a1fd",
        decimals: 18,
      },
      {
        title: "HMDX",
        address: "0x79b1F2AA77F481805b258C7bC58F0F2bF32E151f",
        decimals: 18,
      },
      {
        title: "USDTHECO",
        address: "0xa71edc38d189767582c38a3145b5873052c3e47a",
        decimals: 18,
      },
    ],
  },
  [chainMapping.AVAX]: {
    id: 43114,
    title: 'Avalanche',
    slug: 'AVAX',
    name: "Avalance",
    scan: "https://snowtrace.io/tx/",
    walletScan: "https://snowtrace.io/address/",
    tokenScan: `https://avascan.info/blockchain/c/address/tokenName/token`,
    coingecko: "avalanche",
    covalent: "AVAX",
    trustwallet: "avalanchec", // c - chain
    hex: "0xA86A",
    symbol: "AVAX",
    nativeCurrency: "AVAX",
    rpc: "https://api.avax.network/ext/bc/C/rpc",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/avalance.svg',
    commonBases: [],
  },
  [chainMapping.FTM]: {
    id: 250,
    title: 'Fantom',
    slug: 'FTM',
    name: "Fantom",
    scan: "https://ftmscan.com/tx/",
    walletScan: "https://ftmscan.com/address/",
    tokenScan: `https://ftmscan.com/token/tokenName`,
    coingecko: "fantom",
    covalent: "fantom-mainnet",
    trustwallet: "fantom",
    hex: "0xFA",
    symbol: "FTM",
    nativeCurrency: "FTM",
    rpc: "https://rpc.ftm.tools",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/fantom.svg',
    commonBases: [],
  },
  [chainMapping.OPTIMISM]: {
    id: 10,
    title: 'Optimism',
    slug: 'OPTIMISM',
    name: "Optimism",
    scan: "https://optimistic.etherscan.io/tx/",
    walletScan: "https://optimistic.etherscan.io/address/",
    tokenScan: `https://optimistic.etherscan.io/address/tokenName`,
    coingecko: "optimism",
    covalent: "optimism-mainnet",
    trustwallet: "optimism",
    hex: "0xA",
    symbol: "OPTIMISM",
    nativeCurrency: "ETH",
    rpc: "https://mainnet.optimism.io",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/optimism.svg',
    commonBases: [],
  },
  [chainMapping.ARBITRUM]: {
    id: 42161,
    title: 'Arbitrum',
    slug: 'ARBITRUM',
    name: "Arbitrum One",
    scan: "https://arbiscan.io/tx/",
    walletScan: "https://arbiscan.io/address/",
    tokenScan: `https://arbiscan.io/token/tokenName`,
    coingecko: "arbitrum",
    covalent: "arbitrum-mainnet",
    trustwallet: "arbitrum",
    hex: "0xA4B1",
    symbol: "ARBITRUM",
    nativeCurrency: "ETH",
    rpc: "https://arb1.arbitrum.io/rpc",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/arbitrum.svg',
    commonBases: [],
  },
  [chainMapping.ARBITRUM_SEPOLIA_TESTNET]: {
    id: 421614,
    title: 'Arbitrum Sepolia Testnet',
    slug: 'ARBITRUM_SEPOLIA_TESTNET',
    name: "Arbitrum Sepolia Testnet",
    scan: "https://sepolia.arbiscan.io/tx/",
    walletScan: "https://sepolia.arbiscan.io/address/",
    tokenScan: `https://sepolia.arbiscan.io/token/tokenName`,
    coingecko: "",
    covalent: "",
    trustwallet: "",
    hex: "0x66eee",
    symbol: "ARBITRUM_SEPOLIA_TESTNET",
    nativeCurrency: "ETH",
    rpc: "https://sepolia-rollup.arbitrum.io/rpc",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/arbitrum.svg',
    commonBases: [],
  },
  [chainMapping.GNOSIS]: {
    id: 100,
    title: 'Gnosis',
    slug: 'GNOSIS',
    name: "Gnosis",
    scan: "https://blockscout.com/xdai/mainnet/tx/",
    walletScan: "https://blockscout.com/xdai/mainnet/address/",
    tokenScan: `https://blockscout.com/xdai/mainnet/token/tokenName`,
    coingecko: "gnosis",
    covalent: "gnosis-mainnet",
    trustwallet: "gnosis",
    hex: "0x64",
    symbol: "GNOSIS",
    nativeCurrency: "ETH",
    rpc: "https://rpc.gnosischain.com",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/gnosis.svg',
    commonBases: [],
  },
  [chainMapping.KLAYTN]: {
    id: 8217,
    title: 'Klaytn',
    slug: 'KLAYTN',
    name: "Klaytn",
    scan: "https://scope.klaytn.com/tx/",
    walletScan: "https://scope.klaytn.com/address/",
    tokenScan: `https://scope.klaytn.com/token/tokenName`,
    coingecko: "klaytn",
    covalent: "klaytn-mainnet",
    trustwallet: "klaytn",
    hex: "0x2019",
    symbol: "KLAYTN",
    nativeCurrency: "ETH",
    rpc: "https://public-node-api.klaytnapi.com/v1/cypress",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/klaytn.svg',
    commonBases: [],
  },
  [chainMapping.AURORA]: {
    id: 1313161554,
    title: 'Aurora',
    slug: 'AURORA',
    name: "Aurora",
    scan: "https://aurorascan.dev/tx/",
    walletScan: "https://aurorascan.dev/address/",
    tokenScan: `https://aurorascan.dev/token/tokenName`,
    coingecko: "aurora",
    covalent: "aurora-mainnet",
    trustwallet: "aurora",
    hex: "0x4E454152",
    symbol: "AURORA",
    nativeCurrency: "ETH",
    rpc: "https://mainnet.aurora.dev",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/aurora.svg',
    commonBases: [],
  },
  [chainMapping.ELROND]: {
    id: 999999,
    title: 'MultiversX',
    slug: 'ELROND',
    name: "MultiversX",
    scan: "https://explorer.multiversx.com/transactions/",
    walletScan: "https://explorer.multiversx.com/accounts/",
    tokenScan: `https://xexchange.com/analytics/tokens/tokenName`,
    coingecko: "elrond",
    covalent: 'EGLD',
    trustwallet: '',
    hex: "0x999999",
    symbol: "ELROND",
    nativeCurrency: "EGLD",
    rpc: "https://api.elrond.com",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/elrond.svg',
    commonBases: [],
  },
  [chainMapping.BTTC]: {
    id: 199,
    title: 'BTTC',
    slug: 'BTTC',
    name: "BitTorrent Chain Mainnet",
    scan: "https://scan.bt.io/#/transaction/",
    walletScan: "https://scan.bt.io/#/address/",
    tokenScan: `https://scan.bt.io/#/token20/tokenName`,
    coingecko: "bittorrent",
    covalent: '',
    trustwallet: 'tron',
    hex: "0xC7",
    symbol: "BTTC",
    nativeCurrency: "BT",
    rpc: "https://rpc.bt.io",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/bttc.svg',
    commonBases: [{
      title: 'BTT',
      address: '0x0000000000000000000000000000000000001010',
      decimals: 18
    }],
  },
  [chainMapping.TRON]: {
    id: 99999999,
    title: 'TRON',
    slug: 'TRON',
    name: "Tron",
    scan: "https://tronscan.org/#/transaction/",
    walletScan: "https://tronscan.org/#/contract/",
    tokenScan: `https://tronscan.org/#/token20/tokenName`,
    coingecko: "tron",
    covalent: '',
    trustwallet: '',
    hex: "0x99999999",
    symbol: "TRON",
    nativeCurrency: "TRX",
    rpc: "https://api.trongrid.io",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/tron.svg',
    commonBases: [],
  },
  [chainMapping.OKX]: {
    id: 66,
    title: 'OKT Chain',
    slug: 'OKX',
    name: "OKXChain Mainnet",
    scan: "https://oklink.com/okc/tx/",
    walletScan: "https://www.oklink.com/okc/address/",
    tokenScan: `https://www.oklink.com/okc/token/tokenName`,
    coingecko: "oec-ecosystem",
    covalent: '',
    trustwallet: 'okxchain',
    hex: "0x42",
    symbol: "OKX",
    nativeCurrency: "OKT",
    rpc: "https://exchainrpc.okex.org",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/okex.png',
    commonBases: [],
  },
  [chainMapping.MOONBEAM]: {
    id: 1284,
    title: 'Moonbeam',
    slug: 'MOONBEAM',
    name: "Moonbeam",
    scan: "https://moonscan.io/tx/",
    walletScan: "https://moonscan.io/address/",
    tokenScan: `https://moonscan.io/token/tokenName`,
    coingecko: "",
    covalent: 'moonbeam-mainnet',
    trustwallet: '',
    hex: "0x504",
    symbol: "MOONBEAM",
    nativeCurrency: "GLMR",
    rpc: "https://rpc.api.moonbeam.network",
    image: 'https://kattana-icons.s3.amazonaws.com/moonbeam.svg',
    commonBases: [],
  },
  [chainMapping.CRONOS]: {
    id: 25,
    title: 'Cronos',
    slug: 'CRONOS',
    name: "Cronos Mainnet Beta",
    scan: "https://cronoscan.com/tx/",
    walletScan: "https://cronoscan.com/address/",
    tokenScan: `https://cronoscan.com/token/tokenName`,
    coingecko: "",
    covalent: 'cronos-mainnet',
    trustwallet: '',
    hex: "0x19",
    symbol: "CRONOS",
    nativeCurrency: "CRO",
    rpc: "https://cronos-evm.publicnode.com",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/cronos.svg',
    commonBases: [],
  },
  [chainMapping.ZKSYNC]: {
    id: 324,
    title: 'zkSync',
    slug: 'ZKSYNC',
    name: "zkSync Era Mainnet",
    scan: "https://explorer.zksync.io/tx/",
    walletScan: "https://explorer.zksync.io/address/",
    tokenScan: `https://explorer.zksync.io/address/tokenName`,
    coingecko: "",
    covalent: '',
    trustwallet: '',
    hex: "0x144",
    symbol: "ZKSYNC",
    nativeCurrency: "ETH",
    rpc: "https://mainnet.era.zksync.io",
    image: 'https://kattana-icons.s3.amazonaws.com/zkSync.svg',
    commonBases: [],
  },
  [chainMapping.HARMONY]: {
    id: 1666600000,
    title: 'Harmony',
    slug: 'HARMONY',
    name: "Harmony Mainnet Shard 0",
    scan: "https://explorer.harmony.one/tx/",
    walletScan: "https://explorer.harmony.one/address/",
    tokenScan: `https://explorer.harmony.one/address/tokenName`,
    coingecko: "",
    covalent: 'harmony-mainnet',
    trustwallet: '',
    hex: "0x63564c40",
    symbol: "HARMONY",
    nativeCurrency: "ONE",
    rpc: "https://api.harmony.one",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/harmony.svg',
    commonBases: [],
  },
  [chainMapping.KAVA]: {
    id: 2222,
    title: 'Kava',
    slug: 'KAVA',
    name: "Kava EVM",
    scan: "https://explorer.kava.io/tx/",
    walletScan: "https://explorer.kava.io/address/",
    tokenScan: `https://explorer.kava.io/token/tokenName`,
    coingecko: "",
    covalent: '',
    trustwallet: '',
    hex: "0x8ae",
    symbol: "KAVA",
    nativeCurrency: "KAVA",
    rpc: "https://evm2.kava.io",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/kava.svg',
    commonBases: [],
  },
  [chainMapping.CANTO]: {
    id: 7700,
    title: 'CANTO',
    slug: 'CANTO',
    name: "CANTO EVM",
    scan: "https://tuber.build/tx/",
    walletScan: "https://tuber.build/address/",
    tokenScan: `https://tuber.build/token/tokenName`,
    coingecko: "",
    covalent: '',
    trustwallet: '',
    hex: "0x1e14",
    symbol: "CANTO",
    nativeCurrency: "CANTO",
    rpc: "https://canto.slingshot.finance",
    image: 'https://kattana-icons.s3.amazonaws.com/CANTO.svg',
    commonBases: [],
  },
  [chainMapping.PULSECHAIN]: {
    id: 369,
    title: 'PulseChain',
    slug: 'PULSECHAIN',
    name: "PulseChain",
    scan: "https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/tx/",
    walletScan: "https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/address/",
    tokenScan: `https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/token/tokenName`,
    coingecko: "",
    covalent: '',
    trustwallet: '',
    hex: "0x171",
    symbol: "PULSECHAIN",
    nativeCurrency: "PLS",
    rpc: "https://rpc.pulsechain.com",
    image: 'https://kattana-icons.s3.amazonaws.com/PULSECHAIN.png',
    commonBases: [],
  },
  [chainMapping.BASE]: {
    id: 8453,
    title: 'Base',
    slug: 'BASE',
    name: "Base",
    scan: "https://base.blockscout.com/tx/",
    walletScan: "https://base.blockscout.com/address/",
    tokenScan: `https://base.blockscout.com/token/tokenName`,
    coingecko: "",
    covalent: 'base-mainnet',
    trustwallet: '',
    hex: "0x2105",
    symbol: "BASE",
    nativeCurrency: "ETH",
    rpc: "https://base.meowrpc.com",
    image: 'https://kattana-icons.s3.amazonaws.com/BASE.png',
    commonBases: [],
  },
  [chainMapping.TON]: {
    id: 999998,
    title: 'TON',
    slug: 'TON',
    name: "TON",
    scan: "https://tonscan.org/tx/",
    walletScan: "https://tonscan.org/address/",
    tokenScan: `https://tonscan.org/jetton/tokenName`,
    coingecko: "",
    covalent: '',
    trustwallet: '',
    hex: "0x999998",
    symbol: "TON",
    nativeCurrency: "TON",
    rpc: "ADNL",
    image: 'https://kattana-icons.s3.amazonaws.com/TON.svg',
    commonBases: [],
  },
  [chainMapping.ARTHERA]: {
    id: 10242,
    title: 'Arthera',
    slug: 'ARTHERA',
    name: "ARTHERA",
    scan: "https://explorer.arthera.net/tx/",
    walletScan: "https://explorer.arthera.net/address/",
    tokenScan: `https://explorer.arthera.net/token/tokenName`,
    coingecko: "arthera",
    covalent: "arthera-mainnet",
    trustwallet: "arthera",
    hex: "0x2802",
    symbol: "ARTHERA",
    nativeCurrency: "AA",
    rpc: "https://rpc.arthera.net",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/arthera.svg',
    commonBases: [],
  },
  [chainMapping.ARTHERA_TESTNET]: {
    id: 10243,
    title: 'Arthera Testnet',
    slug: 'ARTHERA_TESTNET',
    name: "Arthera Testnet",
    scan: "https://explorer-test.arthera.net/tx/",
    walletScan: "https://explorer-test.arthera.net/address/",
    tokenScan: `https://explorer-test.arthera.net/address/tokenName`,
    coingecko: "",
    covalent: '',
    trustwallet: '',
    hex: "0x2803",
    symbol: "ARTHERA_TESTNET",
    nativeCurrency: "AA",
    rpc: "https://rpc-test.arthera.net",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/arthera.png',
    commonBases: [],
  },
  [chainMapping.MANTA]: {
    id: 169,
    title: 'Manta',
    slug: 'MANTA',
    name: "Manta Pacific Mainnet",
    scan: "https://pacific-explorer.manta.network/tx/",
    walletScan: "https://pacific-explorer.manta.network/address/",
    tokenScan: `https://pacific-explorer.manta.network/token/tokenName`,
    coingecko: "manta-network",
    covalent: '',
    trustwallet: '',
    hex: "0xa9",
    symbol: "MANTA",
    nativeCurrency: "ETH",
    rpc: "https://pacific-rpc.manta.network/http",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/manta-pacific.png',
    commonBases: [],
  },
  [chainMapping.METIS]: {
    id: 1088,
    title: 'Metis',
    slug: 'METIS',
    name: "Metis Andromeda Mainnet",
    scan: "https://andromeda-explorer.metis.io/tx/",
    walletScan: "https://andromeda-explorer.metis.io/address/",
    tokenScan: `https://andromeda-explorer.metis.io/token/tokenName`,
    coingecko: "metis-andromeda",
    covalent: 'metis-mainnet',
    trustwallet: '',
    hex: "0x440",
    symbol: "METIS",
    nativeCurrency: "METIS",
    rpc: "https://metis-pokt.nodies.app",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/metisdao-metis-logo.svg',
    commonBases: [],
  },
  [chainMapping.CONFLUX]: {
    id: 1030,
    title: 'Conflux',
    slug: 'CONFLUX',
    name: "Conflux eSpace",
    scan: "https://evm.confluxscan.io/tx/",
    walletScan: "https://evm.confluxscan.io/address/",
    tokenScan: `https://evm.confluxscan.io/token/tokenName`,
    coingecko: "conflux",
    covalent: '',
    trustwallet: '',
    hex: "0x406",
    symbol: "CONFLUX",
    nativeCurrency: "CFX",
    rpc: "https://evm.confluxrpc.com",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/conflux-logo.svg',
    commonBases: [],
  },
  [chainMapping.LINEA]: {
    id: 59144,
    title: 'Linea',
    slug: 'LINEA',
    name: "Linea Mainnet",
    scan: "https://lineascan.build/transaction/",
    walletScan: "https://lineascan.build/address/",
    tokenScan: `https://lineascan.build/token/tokenName`,
    coingecko: "linea",
    covalent: '',
    trustwallet: '',
    hex: "0xe708",
    symbol: "LINEA",
    nativeCurrency: "ETH",
    rpc: "https://rpc.linea.build",
    image: 'https://kattana-icons.s3.amazonaws.com/chains/linea.webp',
    commonBases: [],
  },
  [chainMapping.SOLANA]: {
    id: 1399811149,
    title: 'Solana',
    slug: 'SOLANA',
    name: "Solana",
    scan: "https://solscan.io/tx/",
    walletScan: "https://solscan.io/account/",
    tokenScan: `https://solscan.io/token/tokenName`,
    coingecko: "solana",
    covalent: 'solana-mainnet',
    trustwallet: 'solana',
    hex: "0x536F6C4D",
    symbol: "SOLANA",
    nativeCurrency: "SOL",
    rpc: "https://api.mainnet-beta.solana.com",
    image: 'https://kattana-icons.s3.amazonaws.com/SOL-nobg.svg',
    commonBases: [],
  },
};

export { mapping };
